import React, { Dispatch, SetStateAction, useState, ChangeEvent, useCallback, useMemo, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { toast } from 'react-toastify';
import { isNumericSpecialChars, isRepeatedChars, isRequiredChars, isRequiredLength } from '@shared/utils/common';
import { useAdminsControllerChangeInitialPasswordV1, useAdminsControllerChangePasswordV1 } from '@api/generated/admin-auth/admin-auth';
import { useAuthStore } from '@store/useAuthStore';
import theme from '@/styles/theme';
import ModalContainer, { ButtonGroup } from '../modal/ModalContainer';
import StyledButton from '../atoms/StyledButton';
import StyledPassword from '@/shared/components/atoms/StyledPassword';

type PasswordChangeModalProps = {
	isInit?: boolean;
	setPasswordModal?: Dispatch<SetStateAction<boolean>>;
}

const PasswordChangeModal = ({
	isInit,
	setPasswordModal
}: PasswordChangeModalProps) => {
	const { logout } = useAuthStore();
	const [inputs, setInputs] = useState({
		currentPassword: '',
		newPassword1: '',
		newPassword2: ''
	});
	
	// 최초 비밀번호 변경
	const { mutate: initialPasswordChangeMutate, isPending: isInitialUpdating } = useAdminsControllerChangeInitialPasswordV1({
		mutation: {
			onSuccess: async () => {
				alert('비밀번호가 성공적으로 변경되었습니다.\n다시 로그인해주세요.');
				logout();
			}
		}
	});
	
	// 비밀번호 변경
	const { mutate: passwordChangeMutate, isPending: passwordUpdating } = useAdminsControllerChangePasswordV1({
		mutation: {
			onSuccess: async () => {
				alert('비밀번호가 성공적으로 변경되었습니다.\n다시 로그인해주세요.');
				logout();
			}
		}
	});
	
	// 유효성 검사
	const { isEightLength, hasRequiredChars, hasNoRepeatedChars, isSamePassword } = useMemo(() => {
		const { newPassword1, newPassword2 } = inputs;
		let isEightLength = null, hasRequiredChars = null, hasNoRepeatedChars = null, isSamePassword = null;
		
		if (newPassword1.length === 0) {
			return {
				isEightLength,
				hasRequiredChars,
				hasNoRepeatedChars,
				isSamePassword
			};
		}
		
		isEightLength = isRequiredLength(newPassword1);
		hasRequiredChars = isRequiredChars(newPassword1) || isNumericSpecialChars(newPassword1);
		hasNoRepeatedChars = newPassword1.length !== 0 && !isRepeatedChars(newPassword1);
		isSamePassword = newPassword1.length !== 0 && newPassword1 === newPassword2;
		
		return {
			isEightLength,
			hasRequiredChars,
			hasNoRepeatedChars,
			isSamePassword
		};
	}, [inputs]);
	
	const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		
		setInputs({
			...inputs,
			[name]: value
		});
	}, [inputs]);
	
	const handleClose = useCallback(() => {
		if (setPasswordModal) setPasswordModal(false);
	}, []);
	
	const handleSave = useCallback(() => {
		if (!isEightLength || !hasRequiredChars || !hasNoRepeatedChars || !isSamePassword) {
			toast.error('비밀번호 유효성을 만족하지 않습니다.');
			return;
		}
		
		if (isInit) {
			initialPasswordChangeMutate({
				data: {
					newPassword: inputs.newPassword1
				}
			});
		} else {
			passwordChangeMutate({
				data: {
					currentPassword: inputs.currentPassword,
					newPassword: inputs.newPassword1
				}
			})
		}
	}, [inputs, isEightLength, hasRequiredChars, hasNoRepeatedChars, isSamePassword]);

	return (
		<ModalContainer
			maxWidth={508}
			isLoading={isInitialUpdating || passwordUpdating}
			title="비밀번호 변경"
			onClose={handleClose}>
			<Form>
				{!isInit && (
					<>
	                    <Title>현재 비밀번호</Title>
	                    <StyledPassword
		                    margin="0 0 -16px"
		                    name="currentPassword"
		                    value={inputs.currentPassword}
		                    onChange={onChange}
		                    placeholder="현재 비밀번호"
	                    />
	                </>
				)}
				<Title>새로운 비밀번호</Title>
                <StyledPassword
	                name="newPassword1"
	                value={inputs.newPassword1}
	                onChange={onChange}
	                placeholder="새 비밀번호"
                />
                <StyledPassword
	                margin="12px 0"
	                name="newPassword2"
	                value={inputs.newPassword2}
	                onChange={onChange}
	                onKeyPress={e => e.key === 'Enter' && handleSave()}
	                placeholder="새 비밀번호 확인"
                />
			</Form>
            <ValidateBox>
                <ValidateText $active={isEightLength}>- 비밀번호 8자리 이상</ValidateText>
                <ValidateText $active={hasRequiredChars}>- 대문자, 소문자, 숫자, 특수문자 포함</ValidateText>
                <ValidateText $active={hasNoRepeatedChars}>- 반복되는 문자 사용 금지</ValidateText>
                <ValidateText $active={isSamePassword}>- 비밀번호 확인</ValidateText>
            </ValidateBox>
            <ButtonGroup>
	            {!isInit && (
		            <StyledButton
			            width={140}
			            title="다음에 변경"
			            fontColor={theme.colors.blackColor}
			            margin="0 8px 0 0"
			            border={`1px solid ${theme.colors.lightGrayBorder}`}
			            bgColor={theme.colors.whiteColor}
			            onClick={handleClose}
		            />
	            )}
                <StyledButton
	                title="확인"
	                width={140}
	                fontColor={theme.colors.whiteColor}
	                border="none"
	                bgColor={theme.colors.blackColor}
	                onClick={handleSave}
                />
            </ButtonGroup>
		</ModalContainer>
	);
};

export default PasswordChangeModal;

const Form = styled.form``;
const Title = styled.div`
  margin: 36px 0 12px;
`;
const ValidateBox = styled.div``;
const ValidateText = styled.div<{ $active: boolean | null; }>`
  font-size: 14px;
  color: ${theme.colors.lightGrayFontColor};
  margin-bottom: 6px;

  ${({ $active }) => $active === true && css`
    color: ${theme.colors.activeSkyBlue};
  `};
  ${({ $active }) => $active === false && css`
    color: ${theme.colors.activeOrange};
  `};
`;
